import { InputField } from '@advanza/input'
import { Icon } from '@advanza/ui'
import clsx from 'clsx'
import { format as dateFormat, isValid, parseISO } from 'date-fns'
import { useOutsideClick } from 'hooks/miscHooks'
import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { DayPicker, DayPickerProps } from 'react-day-picker'
import style from './datePicker.module.css'
import rdpStyle from './datePickerRdp.module.css'

/** Display format for the input (e.g. dd-MM-yyyy for locale); value sent to onChange is always yyyy-MM-dd */
const DEFAULT_DISPLAY_FORMAT = 'yyyy-MM-dd'

/** date-fns uses yyyy for calendar year; YYYY is week-numbering year. Normalize so callers can pass either. */
function toDateFnsFormat(formatStr: string): string {
    return formatStr.replace(/YYYY/g, 'yyyy').replace(/DD/g, 'dd').replace(/MM/g, 'MM')
}

interface DatePickerProps {
    value?: string | null
    onChange: (e: ChangeEvent<HTMLInputElement>) => void
    name?: string
    format?: string
    icon?: string
    placeholder?: string
    legend?: boolean
    maxWidth?: string | number
    error?: boolean
    large?: boolean
    alignRight?: boolean
    withIcon?: boolean
    dayPickerProps?: Pick<
        DayPickerProps,
        'disabled' | 'showOutsideDays' | 'defaultMonth' | 'captionLayout' | 'fromYear' | 'toYear'
    >
    opened?: boolean
    readOnlyInputField?: boolean
}

function parseValueToDate(value: string | null | undefined): Date | undefined {
    if (!value || value.length === 0) return undefined
    const iso = typeof value === 'string' && value.includes(' ') ? value.replace(' ', 'T') : value
    const parsed = parseISO(iso)
    return isValid(parsed) ? parsed : undefined
}

const DatePicker = ({
    icon = 'date_range',
    name,
    placeholder,
    legend = false,
    value = null,
    format = DEFAULT_DISPLAY_FORMAT,
    maxWidth,
    error,
    large = true,
    alignRight = false,
    withIcon = true,
    opened = false,
    dayPickerProps,
    onChange,
    readOnlyInputField = false,
    ...rest
}: DatePickerProps) => {
    const [showDayPicker, setShowDayPicker] = useState(false)
    const [query, setQuery] = useState<string | null>(null)
    const ref = useRef(null)

    const dateValue = parseValueToDate(value)
    const displayFormat = toDateFnsFormat(format || DEFAULT_DISPLAY_FORMAT)
    const formattedDay = dateValue ? dateFormat(dateValue, displayFormat) : ''

    useEffect(() => {
        setQuery(null)
    }, [value])

    const handleSelect = (day: Date | undefined) => {
        try {
            const val = day && isValid(day) ? dateFormat(day, 'yyyy-MM-dd') : null
            onChange({
                target: { name, value: val },
            } as ChangeEvent<HTMLInputElement>)
            setQuery(val)
            setShowDayPicker(false)
        } catch (err) {
            setShowDayPicker(false)
            throw err instanceof Error ? err : new Error(String(err))
        }
    }

    useOutsideClick(ref, () => {
        setShowDayPicker(false)
    })

    const currentYear = new Date().getFullYear()
    const pickerProps = {
        showOutsideDays: true,
        captionLayout: 'dropdown-buttons' as const,
        fromYear: currentYear - 20,
        toYear: currentYear + 1,
        ...dayPickerProps,
    }
    const valid = !!dateValue
    const classString = clsx(
        style.root,
        valid && style.valid,
        error && style.hasError,
        large && style.large,
        alignRight && style.alignRight,
    )

    const inputDisplayValue =
        query === null
            ? formattedDay
            : (() => {
                  const qDate = parseValueToDate(query)
                  return qDate ? dateFormat(qDate, displayFormat) : query
              })()

    return (
        <>
            <div className={classString} ref={ref}>
                <InputField
                    before={withIcon && <Icon name={icon} />}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault()
                            handleSelect(query ? parseValueToDate(query) : undefined)
                        }
                    }}
                    onFocus={() => setShowDayPicker(true)}
                    onChange={(e) => {
                        const val = e.target.value
                        setQuery(val || null)
                    }}
                    name={name}
                    placeholder={placeholder || name}
                    legend={legend}
                    error={error}
                    style={{ maxWidth }}
                    {...rest}
                    disabled={false}
                    value={inputDisplayValue}
                    readOnly={readOnlyInputField}
                    type="text"
                />
                {showDayPicker && (
                    <DayPicker
                        mode="single"
                        selected={dateValue}
                        onSelect={handleSelect}
                        defaultMonth={dateValue ?? new Date()}
                        className={clsx(
                            style.dayPicker,
                            opened && style.opened,
                            dateValue && !legend && style.withValue,
                        )}
                        classNames={rdpStyle}
                        {...pickerProps}
                    />
                )}
            </div>
        </>
    )
}

export default DatePicker
